import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { CheckCircle2, XCircle, Info, X } from "lucide-react";
import ClipLoader from "react-spinners/ClipLoader";

// Create the popup context
const ApiPopupContext = createContext(null);

export const ApiPopupProvider = ({ children }) => {
  // Current toast state
  const [toast, setToast] = useState(null);

  // Store timeout id to avoid multiple active timers
  const timerRef = useRef(null);

  /**
   * Clear the current popup and any pending timeout.
   */
  const clear = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    setToast(null);
  }, []);

  /**
   * Display a popup.
   *
   * @param {"success"|"error"|"info"|"pending"} type
   * @param {string} message
   * @param {number} ms Duration before auto-close.
   *                    Use 0 to keep the popup visible.
   */
  const show = useCallback((type, message, ms = 3000) => {
    // Remove previous timer if one exists
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // Display popup
    setToast({ type, message });

    // Only auto-close if duration is greater than zero
    if (ms > 0) {
      timerRef.current = setTimeout(() => {
        setToast(null);
        timerRef.current = null;
      }, ms);
    }
  }, []);

  /**
   * Cleanup timer when provider unmounts.
   */
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  /**
   * Expose popup API.
   */
  const api = useMemo(
    () => ({
      success: (message, ms) => show("success", message, ms),
      error: (message, ms) => show("error", message, ms),
      info: (message, ms) => show("info", message, ms),

      // Pending notifications stay open by default
      pending: (message, ms = 0) => show("pending", message, ms),

      clear,
    }),
    [show, clear]
  );

  return (
    <ApiPopupContext.Provider value={api}>
      {children}

      <ApiPopup
        toast={toast}
        onClose={clear}
      />
    </ApiPopupContext.Provider>
  );
};

/**
 * Hook to access popup API.
 */
export const useApiPopup = () => {
  const context = useContext(ApiPopupContext);

  if (!context) {
    throw new Error(
      "useApiPopup must be used inside <ApiPopupProvider />"
    );
  }

  return context;
};

/**
 * Small loading spinner used for pending notifications.
 */
const Spinner = ({ className = "" }) => (
  <span
    className={`mt-[2px] ${className}`}
    aria-label="Loading"
  >
    <ClipLoader size={16} />
  </span>
);

/**
 * Popup component.
 */
const ApiPopup = ({ toast, onClose }) => {
  const isVisible = Boolean(toast);

  const base =
    "fixed top-4 left-1/2 -translate-x-1/2 z-[9999] " +
    "min-w-[280px] max-w-[520px] px-4 py-3 rounded-lg shadow-md " +
    "border text-sm flex items-start justify-between gap-3 " +
    "transition-all duration-300";

  const visibility = isVisible
    ? "opacity-100 translate-y-0"
    : "opacity-0 -translate-y-4 pointer-events-none";

  const theme =
    toast?.type === "success"
      ? "bg-green-50 border-green-200 text-green-900"
      : toast?.type === "error"
      ? "bg-red-50 border-red-200 text-red-900"
      : toast?.type === "pending"
      ? "bg-blue-50 border-blue-200 text-blue-900"
      : "bg-stone-50 border-stone-200 text-stone-900";

  const Icon =
    toast?.type === "success"
      ? CheckCircle2
      : toast?.type === "error"
      ? XCircle
      : Info;

  return (
    <div
      role={toast?.type === "error" ? "alert" : "status"}
      aria-live="polite"
      className={`${base} ${theme} ${visibility}`}
    >
      <div className="flex items-start gap-2">
        {toast?.type === "pending" ? (
          <Spinner />
        ) : (
          <Icon
            size={18}
            className="mt-[1px]"
          />
        )}

        <div className="leading-snug">
          {toast?.message}
        </div>
      </div>

      <button
        type="button"
        onClick={onClose}
        title="Close"
        aria-label="Close notification"
        className="opacity-70 hover:opacity-100"
      >
        <X size={16} />
      </button>
    </div>
  );
};